import React, {useState, useEffect} from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Card, Grid2 as Grid, Link, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';


//import schedules
import ReportAmendReasonBlock from '../components/ReportAmendReasonBlock';
import depositReportContent from '../../../types/reports/depositReportContent';
import ReceiptsScheduleTable from '../components/ReceiptsScheduleTable';
import { ReceiptItem } from '../../../types/reports/receiptItem';
import MerchantProviderFeesScheduleTable from '../components/MerchantProviderFeesScheduleTable';
import { MerchantProviderFee } from '../../../types/reports/merchantProviderFee';
import ReportAmendedReasonBlock from '../components/ReportAmendedReasonBlock';
import ChangeLogScheduleTable from '../components/ChangeLogScheduleTable';
import changeLogCategory from '../../../types/reports/changeLogCategory';
import ChangeLogHistory from '../components/ChangeLogHistory';
import reportChangeLog from '../../../types/reports/reportChangeLog';

interface ReportProps {
   reportId: number;
   currentReport?: any;
}

const DepositReport = (reportProps: ReportProps): JSX.Element => {

    const theme = useTheme();
    const { showBoundary } = useErrorBoundary();  
    const [scheduleValue, setScheduleValue] = useState('summary');
    const fieldWidth = { xs: "60%", sm: "40%", md: "35%" };

    const [tableRows, setRows] = useState<ReceiptItem[]>();
    const [merchantProviderFees, setMerchantProviderFees] = useState<MerchantProviderFee[]>();
    const [refundedContributions, setRefundedContributions] = useState<MerchantProviderFee[]>();
    const [diffs, setDiffs] = useState<changeLogCategory[]>();
    const [diffsCount, setDiffsCount] = useState<number>();
    const [report, setReport] = useState<depositReportContent>(reportProps.currentReport ?? {});
    const [reportChangeLogs, setReportChangeLogs] = useState<reportChangeLog[]>();

    useEffect(() => {
            if(reportProps.currentReport){
                let report = reportProps.currentReport;
                setScheduleValue('summary');
                setReport(report);
                setRows(report.receipts);
                setMerchantProviderFees(report.merchantProviderFees);
                setRefundedContributions(report.refundedContributions);
                setDiffs(report.diffs);
            }
            else{
                getFullReport();
            }
            getReportDiffs();
    }, [reportProps.reportId])

    const getFullReport = async () => {

        try{
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/${reportProps.reportId}`)
            .then(result => {
            if (!result.ok) {
                showBoundary(new Error('Failed to get the deposit report content.'));
            }
            return result.json();
        })
        .then(result => {

            let report = result;
            setScheduleValue('summary');
            setReport(report);
            setRows(report.receipts);
            setMerchantProviderFees(report.merchantProviderFees);
            setRefundedContributions(report.refundedContributions);
            setDiffs(report.diffs);
        });
        }catch(error){
            console.error('Uncaught Error:', error);
            showBoundary(new Error(`${error}`));
        }
    }

    const getReportDiffs = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/report/diffs/${reportProps.reportId}`);
            if (!response.ok) {
                showBoundary(new Error('Failed to get report change log.'));
            }
            const diffs = await response.json();
            setReportChangeLogs(diffs);
        } catch (error) {
            console.error('Uncaught Error:', error);
        }
    }

    const handleScheduleChange = (event: React.SyntheticEvent, newValue: string) => {
        setScheduleValue(newValue);
    };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <TabContext value={scheduleValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleScheduleChange} aria-label="deposit report schedules">
            <Tab className="report-tab-label" label="Summary" value="summary" />
            {
                (report?.receipts?.length ?? 0) > 0 &&
                <Tab className="report-tab-label" label={`Deposit Items [${report?.receipts?.length}]`} value="receipts" />
            }
            {
                 (report?.merchantProviderFees?.length ?? 0) > 0 &&
                 <Tab className="report-tab-label" label={`Merchant Provider Fees [${report?.merchantProviderFees?.length}]`} value="fees" />
            }
            {
                 (report?.refundedContributions?.length ?? 0) > 0 &&
                 <Tab className="report-tab-label" label={`Refunded Contributions [${report?.refundedContributions?.length}]`} value="refunds" />
            }
            {
                 report?.diffs && report.isAmendment && report.isAmended && // not the latest report, staying the same
                 <Tab className="report-tab-label" label={`Amended Items`} value="changes"/>
            }
            {
                  !report?.isAmended && reportChangeLogs && reportChangeLogs.length > 0 && // latest report
                 <Tab className="report-tab-label" label={`Change Log`} value="logs"/>
            }
          </TabList>
        </Box>
        <TabPanel value="summary">
            <Grid container spacing={2}>
                {/*Filer Info*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filer:
                                </Typography>
                                <Typography component={Link} variant={'body1'} underline='hover' target="_blank" color="primary" href={`/filers?q=${report?.cpfId}`} fontWeight={550}>
                                     {report?.filerFullName} ({report?.cpfId})
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Filed On:
                                </Typography>
                                <Typography variant={'body1'}>
                                     {report?.dateFiled}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2}>
                                <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                     Deposit Date:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} color="primary">
                                     {report?.reportingPeriod}
                                </Typography>
                            </Stack>
                            {
                                report?.isCandidateReport && report?.officeDistrictSought &&
                                <Stack direction="row" spacing={1}>
                                    <Typography component={"i"} variant={'body1'} width={fieldWidth}>
                                       Office/District Sought:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={500}>
                                        {report?.officeDistrictSought}
                                    </Typography>
                                </Stack>
                            }
                        </Box>
                    </Box>
                </Grid>
                {/*Amendment Reason*/}
                {
                    report?.isAmendment && 
                    <ReportAmendReasonBlock reason={report?.amendmentReason} previousReportId={report?.previousReportId}></ReportAmendReasonBlock>
                }
                {/*Being Amended*/}
                {
                    report?.isAmended && 
                    <ReportAmendedReasonBlock nextReportId={report?.nextReportId}></ReportAmendedReasonBlock>
                }
                {/*Financial Summary*/}
                <Grid size={12}>
                    <Box
                        component={Card}
                        boxShadow={0}
                        sx={{ bgcolor: 'alternate.light' }}
                        border={`1px solid ${theme.palette.divider}`}
                    >
                        <Box padding={{ xs: 2, sm: 3 }}>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Total Itemized Receipts:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.receiptItemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Total Unitemized Receipts:
                                </Typography>
                                <Typography variant={'body1'} align='right'>
                                     {report?.receiptUnitemizedTotal}
                                </Typography>
                            </Stack>
                            <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                <Typography component={"i"} variant={'body1'}>
                                    Total Receipts:
                                </Typography>
                                <Typography variant={'body1'} fontWeight={550} align='right'>
                                     {report?.receiptTotal}
                                </Typography>
                            </Stack>
                            { (report?.merchantProviderFees?.length ?? 0) > 0 &&
                                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                    <Typography component={"i"} variant={'body1'}>
                                        - Merchant Provider Fees:
                                    </Typography>
                                    <Typography variant={'body1'} align='right'>
                                        {report?.merchantProviderFeeTotal} 
                                    </Typography>
                                </Stack>
                            }
                            {
                                (report?.refundedContributions?.length ?? 0) > 0 &&
                                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                    <Typography component={"i"} variant={'body1'}>
                                        - Refunded Contributions:
                                    </Typography>
                                    <Typography variant={'body1'} align='right'>
                                        {report?.refundedContributionTotal} 
                                    </Typography>
                                </Stack>
                            }
                            {
                                (report?.merchantProviderFeeTotal || report?.refundedContributionTotal) &&
                                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                                    <Typography component={"i"} fontWeight={550} variant={'body1'}>
                                        = Net Deposit:
                                    </Typography>
                                    <Typography variant={'body1'} fontWeight={550} align='right'>
                                        {report?.netDepositTotal}
                                    </Typography>
                                </Stack>
                            }
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </TabPanel>
        <TabPanel value="receipts">
            <ReceiptsScheduleTable tableRows={tableRows}></ReceiptsScheduleTable>
            <Box padding={{ xs: 2, sm: 3, md: "10px" }}>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                            Total Itemized Receipts:
                    </Typography>
                    <Typography variant={'body1'} align='right'>
                            {report?.receiptItemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Total Unitemized Receipts:
                    </Typography>
                    <Typography variant={'body1'} align='right'>
                        {report?.receiptUnitemizedTotal}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={1} marginBottom={2} display={'flex'} justifyContent={'space-between'}>
                    <Typography variant={'body1'}>
                        Total Receipts:
                    </Typography>
                    <Typography variant={'body1'} fontWeight={550} align='right'>
                        {report?.receiptTotal}
                    </Typography>
                </Stack>
            </Box>
        </TabPanel>
        <TabPanel value="fees">
            <MerchantProviderFeesScheduleTable merchantProviderFees={merchantProviderFees} total={report?.merchantProviderFeeTotal}></MerchantProviderFeesScheduleTable>
        </TabPanel>
        <TabPanel value="refunds">
            <MerchantProviderFeesScheduleTable merchantProviderFees={refundedContributions} total={report?.refundedContributionTotal}></MerchantProviderFeesScheduleTable>
        </TabPanel>
        <TabPanel value="changes">
            <ChangeLogScheduleTable changes={diffs}></ChangeLogScheduleTable>
        </TabPanel>
        <TabPanel value="logs">
            <ChangeLogHistory changes={reportChangeLogs}></ChangeLogHistory>
        </TabPanel>
      </TabContext>
    </Box>
  );
}

export default DepositReport;